import React, { useCallback, useEffect, useRef, useState } from 'react'
import { styled } from 'linaria/react'
import { cx } from 'linaria'
import { NavArrowDown } from 'iconoir-react'

const FilterDropdownWrapper = styled.div`
   position: relative;

   .filter-dropdown-toggle {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      height: 44px;
      padding: 0 0.85rem;
      background-color: var(--color-white);
      color: var(--color-black);
      border: 1px solid var(--color-separator);
      border-radius: 999px;
      font-family: var(--font-industry);
      font-weight: 700;
      font-size: 1rem;
      white-space: nowrap;
      cursor: pointer;
      transition: background-color 0.15s ease, border-color 0.15s ease;

      &:hover {
         border-color: var(--color-vb-blue);
      }

      &:focus-visible {
         outline: 2px solid var(--color-vb-blue-darker);
         outline-offset: 2px;
      }
   }

   &.is-open .filter-dropdown-toggle,
   .filter-dropdown-toggle.is-active {
      background-color: var(--color-vb-blue);
      border-color: var(--color-vb-blue);
      color: var(--color-white);
   }

   .filter-dropdown-chevron {
      transition: transform 0.2s ease;
   }

   &.is-open .filter-dropdown-chevron {
      transform: rotate(180deg);
   }

   .filter-dropdown-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      border-radius: 999px;
      background-color: var(--color-white);
      color: var(--color-vb-blue);
      font-size: 0.75rem;
   }

   .filter-dropdown-panel {
      position: absolute;
      top: calc(100% + 8px);
      z-index: 100;
      min-width: 100%;
      background-color: var(--color-white);
      border: 1px solid var(--color-separator);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
   }

   &.align-left .filter-dropdown-panel {
      left: 0;
   }

   &.align-right .filter-dropdown-panel {
      right: 0;
   }
`

/**
 * Generic dropdown with a toggle button and a floating panel. Closes on outside click and Escape.
 *
 * @param {React.ReactNode} label - Content of the toggle button.
 * @param {number} badgeCount - Optional count shown in a badge next to the label.
 * @param {boolean} isActive - Highlights the toggle when the filter has a value.
 * @param {'left'|'right'} align - Which edge of the toggle the panel aligns to.
 * @param {function} children - Panel content. Called with `{ close }`.
 */
const FilterDropdown = ({
   label,
   badgeCount,
   isActive = false,
   align = 'left',
   panelWidth,
   className,
   children,
}) => {
   const [isOpen, setIsOpen] = useState(false)
   const wrapperRef = useRef(null)
   const toggleRef = useRef(null)

   const close = useCallback(() => setIsOpen(false), [])

   useEffect(() => {
      if (!isOpen) return

      const handleClickOutside = e => {
         if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setIsOpen(false)
         }
      }
      const handleKeyDown = e => {
         if (e.key === 'Escape') {
            setIsOpen(false)
            toggleRef.current?.focus()
         }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
      return () => {
         document.removeEventListener('mousedown', handleClickOutside)
         document.removeEventListener('keydown', handleKeyDown)
      }
   }, [isOpen])

   return (
      <FilterDropdownWrapper
         ref={wrapperRef}
         className={cx(isOpen && 'is-open', `align-${align}`, className)}
      >
         <button
            ref={toggleRef}
            type="button"
            className={cx('filter-dropdown-toggle', isActive && 'is-active')}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(prev => !prev)}
         >
            {label}
            {badgeCount > 0 && (
               <span className="filter-dropdown-badge">{badgeCount}</span>
            )}
            <NavArrowDown
               className="filter-dropdown-chevron"
               width={18}
               height={18}
               strokeWidth={2.5}
            />
         </button>
         {isOpen && (
            <div
               className="filter-dropdown-panel"
               style={panelWidth ? { width: panelWidth } : undefined}
            >
               {children?.({ close })}
            </div>
         )}
      </FilterDropdownWrapper>
   )
}

export default FilterDropdown
